import React, { useEffect, useMemo, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Search, Heart, UserCircle } from 'lucide-react';
import { ADMIN_ROUTE_SLUG } from '@/constants/security';
import { useAuth } from '@/hooks/useAuth';
import { useMyAccessContext } from '@/hooks/security/useMyAccessContext';
import { LEGACY_PUBLIC_LISTING_BASE_PATH, PUBLIC_LISTING_BASE_PATH } from '@/domain/public-properties/urlState';

const PUBLIC_LISTING_PATH = PUBLIC_LISTING_BASE_PATH;

/** Observes a body class toggled by PropiedadesSplitView for /renta mobile chrome coordination. */
function useBodyClassObserver(className: string): boolean {
  const [active, setActive] = useState(() =>
    typeof document !== 'undefined' && document.body.classList.contains(className),
  );

  useEffect(() => {
    if (typeof document === 'undefined') return;

    const sync = () => setActive(document.body.classList.contains(className));
    sync();

    const observer = new MutationObserver(sync);
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, [className]);

  return active;
}

function isCanonicalExploreRoute(pathname: string): boolean {
  const path = (pathname || '').split('?')[0].split('#')[0];
  return path === PUBLIC_LISTING_PATH || path === LEGACY_PUBLIC_LISTING_BASE_PATH || path.startsWith('/filtros');
}

type MobileFooterNavProps = {
  forceVisible?: boolean;
};

const MobileFooterNav: React.FC<MobileFooterNavProps> = ({ forceVisible = false }) => {
  const location = useLocation();
  const currentPath = location.pathname;
  const [isHiddenByScroll, setIsHiddenByScroll] = useState(false);
  // /renta mobile chrome semantics (07-R3 state machine contract):
  //   map_peek / map_collapsed → footer hidden
  //   list_docked              → footer visible
  //   list_immersive           → footer hidden
  const isRentaListDocked = useBodyClassObserver('rc-renta-mobile-list-docked');
  const isRentaListImmersive = useBodyClassObserver('rc-renta-mobile-list-immersive');
  const { isAuthenticated } = useAuth();
  const { accessContext } = useMyAccessContext({ enabled: isAuthenticated });

  const exploreHref = PUBLIC_LISTING_PATH;
  const isExploreActive = isCanonicalExploreRoute(location.pathname);
  const accountDashboardPath = accessContext?.dashboardPath ?? null;
  const accountHref = accountDashboardPath ?? (isAuthenticated ? '/' : '/login');
  const accountLabel = isAuthenticated ? 'Mi cuenta' : 'Iniciar sesión';
  const isAccountActive = accountDashboardPath
    ? currentPath.startsWith(accountDashboardPath)
    : isAuthenticated
      ? currentPath === '/'
      : currentPath.startsWith('/login');

  // Renta listing routes live both at the base path (/renta, /propiedades) AND under
  // SEO subroutes (/renta/casa, /renta/casa/departamento/ciudad-de-mexico/..., up to
  // colonia). Detection MUST treat all of them as listing — otherwise the footer
  // disappears on filtered URLs and the map shows through the bottom inset.
  const isOnRentaListing = useMemo(() => {
    const path = currentPath.split('?')[0].split('#')[0];
    if (path === PUBLIC_LISTING_BASE_PATH || path === LEGACY_PUBLIC_LISTING_BASE_PATH) return true;
    // /renta/<type>/<subtype>/<city>/... — see PUBLIC_LISTING_ROUTE_PATTERNS.
    if (path.startsWith(`${PUBLIC_LISTING_BASE_PATH}/`)) return true;
    return false;
  }, [currentPath]);

  // --- LOGIC: HEURISTICS DE VISIBILIDAD ---
  const isDetailHeuristic = useMemo(() => {
    const path = currentPath.split('?')[0].split('#')[0];
    const segments = path.split('/').filter(Boolean);
    const first = segments[0];

    // Note: 'renta' is blocked here because /renta/<type>/<subtype>/<city>/<municipality>/<colonia>
    // is a LISTING subroute, not a detail page. Without this, the heuristic
    // (segments.length >= 4) would hide the footer on filtered listings.
    const blocked = new Set([
      ADMIN_ROUTE_SLUG,
      'agente',
      'propietario',
      'inquilino',
      'proveedor',
      'propiedades',
      'renta',
      'publicar-propiedad',
      'publicar',
      'register',
      'servicios',
      'promocion-del-mes',
    ]);

    return !!first && !blocked.has(first) && segments.length >= 4;
  }, [currentPath]);

  const showOnlyOn = useMemo(() => {
    const path = currentPath.split('?')[0].split('#')[0];

    if (path === '/') return true;
    if (isOnRentaListing) return true;
    if (path.startsWith('/filtros')) return true;
    if (path.startsWith('/favoritos')) return true;
    if (path.startsWith('/login')) return true;
    return false;
  }, [currentPath, isOnRentaListing]);

  const isTenantGuideRoute = useMemo(() => {
    const path = currentPath.split('?')[0].split('#')[0];
    return path.startsWith('/requisitos-arrendatarios');
  }, [currentPath]);

  const hideFooter =
    !forceVisible && (
      !showOnlyOn ||
      isTenantGuideRoute ||
      currentPath.startsWith('/publicar-propiedad') ||
      isDetailHeuristic
    );

  const showRentaListDockedFooter =
    isOnRentaListing && isRentaListDocked && !isRentaListImmersive;

  const enableScrollHide = !forceVisible && currentPath === '/';

  useEffect(() => {
    if (typeof window === 'undefined') return;

    if (!enableScrollHide) {
      setIsHiddenByScroll(false);
      return;
    }

    let lastScrollY = window.scrollY;
    let accumulatedDelta = 0;
    let ticking = false;

    const topRevealThreshold = 24;
    const hideActivationThreshold = 96;
    const hideTravelThreshold = 30;
    const showTravelThreshold = 18;

    const updateVisibility = () => {
      const currentScrollY = window.scrollY;
      const delta = currentScrollY - lastScrollY;

      if (currentScrollY <= topRevealThreshold) {
        setIsHiddenByScroll(false);
        accumulatedDelta = 0;
        lastScrollY = currentScrollY;
        ticking = false;
        return;
      }

      if (Math.abs(delta) < 2) {
        ticking = false;
        return;
      }

      if (delta > 0) {
        accumulatedDelta = accumulatedDelta > 0 ? accumulatedDelta + delta : delta;

        if (currentScrollY > hideActivationThreshold && accumulatedDelta >= hideTravelThreshold) {
          setIsHiddenByScroll(true);
          accumulatedDelta = 0;
        }
      } else {
        accumulatedDelta = accumulatedDelta < 0 ? accumulatedDelta + delta : delta;

        if (Math.abs(accumulatedDelta) >= showTravelThreshold) {
          setIsHiddenByScroll(false);
          accumulatedDelta = 0;
        }
      }

      lastScrollY = currentScrollY;
      ticking = false;
    };

    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(updateVisibility);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [enableScrollHide]);

  if (hideFooter) return null;

  // On /renta, footer only mounts in list_docked (never map peek/collapsed or immersive).
  if (isOnRentaListing && !showRentaListDockedFooter) return null;

  const isChromeHidden = isHiddenByScroll;

  const navItems = [
    {
      id: 'explorar',
      label: 'Explorar',
      icon: Search,
      to: exploreHref,
      end: false,
    },
    {
      id: 'favoritos',
      label: 'Favoritos',
      icon: Heart,
      to: '/favoritos',
      end: true,
    },
    {
      id: 'account',
      label: accountLabel,
      icon: UserCircle,
      to: accountHref,
      end: true,
    },
  ] as const;

  return (
    <nav
      role="navigation"
      aria-label="Navegación inferior"
      data-mobile-footer-nav="true"
      className={[
        'fixed bottom-0 left-0 right-0 z-50',
        'bg-background/95 backdrop-blur border-t border-border',
        'pb-[env(safe-area-inset-bottom)] md:hidden shadow-sm',
        'will-change-transform transition-[transform,opacity] duration-300 [transition-timing-function:cubic-bezier(0.22,1,0.36,1)] motion-reduce:transition-none',
        isChromeHidden
          ? 'pointer-events-none translate-y-[calc(100%+env(safe-area-inset-bottom))] opacity-0'
          : 'translate-y-0 opacity-100',
      ].join(' ')}
    >
      <div className="mx-auto flex h-16 w-full max-w-md items-center justify-around">
        {navItems.map((item) => {
          const Icon = item.icon;

          if (item.id === 'explorar') {
            return (
              <Link
                key={item.id}
                to={item.to}
                aria-label={item.label}
                className={[
                  'group flex flex-col items-center justify-center w-full h-full',
                  'transition-colors duration-200 ease-in-out',
                  'active:opacity-80 outline-none focus-visible:outline-none',
                  isExploreActive ? 'text-foreground' : 'text-muted-foreground',
                ].join(' ')}
              >
                <div className="relative mb-1">
                  <Icon
                    size={24}
                    strokeWidth={2}
                    className="transition-colors duration-200"
                  />
                </div>

                <span className="text-[10px] tracking-wide font-medium">
                  {item.label}
                </span>
              </Link>
            );
          }

          return (
            <NavLink
              key={item.id}
              to={item.to}
              end={item.end}
              aria-label={item.label}
              className={({ isActive }) =>
                [
                  'group flex flex-col items-center justify-center w-full h-full',
                  'transition-colors duration-200 ease-in-out',
                  'active:opacity-80 outline-none focus-visible:outline-none',
                  (item.id === 'account' ? isAccountActive : isActive)
                    ? item.id === 'account' && isAuthenticated
                      ? 'text-emerald-600'
                      : 'text-foreground'
                    : 'text-muted-foreground',
                ].join(' ')
              }
            >
              <div className="relative mb-1">
                <Icon
                  size={24}
                  strokeWidth={2}
                  className="transition-colors duration-200"
                />
                {item.id === 'account' && isAuthenticated ? (
                  <span className="absolute -right-1 -top-1 h-2.5 w-2.5 rounded-full bg-emerald-500 ring-2 ring-background" />
                ) : null}
              </div>

              <span className={[
                'text-[10px] tracking-wide font-medium',
                item.id === 'account' && isAuthenticated ? 'font-semibold' : '',
              ].join(' ')}>
                {item.label}
              </span>
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
};

export default MobileFooterNav;
